import React from 'react';
import { Col, Row } from 'react-bootstrap';
import ServerError from '../../../../../../components/ServerError';
import SkeletonCard from '../../../../components/Skeleton/Card';
import { EmptyGoals } from '../EmptyGoals';
import { GoalFilter } from '../GoalsFilter/GoalsFilter';
import { GoalsFilterConnected } from '../GoalsFilter/GoalsFilterConnected';
import { GoalBase, GoalId } from '../types';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { useObjectives } from '../useObjectives';
import { GoalsList } from './GoalsList';
import { useGoalViewBy } from '../useGoalViewBy';
import { useGoalWeightValidation } from '../useGoalWeightValidation';

type Props = {
  isReviewGoals?: boolean;
  onCreateGoal: () => void;
  onGoalClick: (goalId: GoalId) => void;
  filter: GoalFilter;
  onFilterChange: (filter: GoalFilter) => void;
};

export const GoalsListWithFilter: React.FC<Props> = ({
  isReviewGoals = false,
  filter,
  onFilterChange,
  onCreateGoal,
  onGoalClick,
}) => {
  const { currentPeriodId, developmentFrameworkEnabled } = useGoalsContext();
  const [viewBy, setViewBy] = useGoalViewBy();
  const goalWeightValidation = useGoalWeightValidation(filter);

  const { goals, loading, error } = useObjectives(
    filter,
    isReviewGoals ? ['employee'] : 'all', // show only employee goals when we are in feedback cycle page
  );

  if (error) {
    return <ServerError />;
  }

  const renderGoals = (goals: GoalBase[]) => {
    if (goals !== undefined && !goals.length) {
      return (
        <EmptyGoals
          createBtnVisible={!!currentPeriodId || !developmentFrameworkEnabled}
          onCreateGoal={onCreateGoal}
        />
      );
    }
    return (
      <GoalsList
        goals={goals}
        onGoalClick={onGoalClick}
        viewBy={viewBy}
        goalWeightValidation={goalWeightValidation}
      />
    );
  };

  return (
    <Row>
      <Col>
        <GoalsFilterConnected
          goals={goals}
          filter={filter}
          onChange={onFilterChange}
          isReviewGoals={isReviewGoals}
          viewBy={viewBy}
          onViewByChange={setViewBy}
          goalWeightValidation={goalWeightValidation}
        />
        {loading || goals === undefined ? (
          <>
            <SkeletonCard height={400} />
            <SkeletonCard height={400} />
          </>
        ) : (
          renderGoals(goals)
        )}
      </Col>
    </Row>
  );
};
